import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

interface WardTATData { ward: string; avgMinutes: number; withinTAT: number; slightlyOver: number; breached: number; totalCompleted: number; }
const TAT_THRESHOLDS = { good: 120, warning: 240 };

export const useWardwiseTATStats = () => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["wardwise-tat-stats-24h", organizationId, currentBranchId],
    queryFn: async (): Promise<WardTATData[]> => {
      if (currentBranchId == null || !organizationId) return [];
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const { data: patients, error } = await supabase
        .from("patients").select("id, ward_name, room, physical_discharge_completion_time, workflow_timestamps")
        .not("ward_name", "is", null).not("physical_discharge_completion_time", "is", null)
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("physical_discharge_completion_time", twentyFourHoursAgo.toISOString());
      if (error) throw error;

      const { data: activities, error: actError } = await supabase
        .from("activities").select("patient_id, workflow_step, started_at, completion_time")
        .in("workflow_step", ["MD", "PD"]).eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("completion_time", twentyFourHoursAgo.toISOString());
      if (actError) throw actError;

      const patientActivities: Record<string, { mdStart?: Date; pdEnd?: Date }> = {};
      (activities || []).forEach((a) => {
        if (!patientActivities[a.patient_id]) patientActivities[a.patient_id] = {};
        if (a.workflow_step === "MD" && a.started_at) patientActivities[a.patient_id].mdStart = new Date(a.started_at);
        if (a.workflow_step === "PD" && a.completion_time) patientActivities[a.patient_id].pdEnd = new Date(a.completion_time);
      });

      const wardMap: Record<string, number[]> = {};
      (patients || []).forEach((p) => {
        const ward = (p as any).room || p.ward_name || "Unknown";
        const patientAct = patientActivities[p.id];
        if (patientAct?.mdStart && patientAct?.pdEnd) {
          const dischargeTime = (patientAct.pdEnd.getTime() - patientAct.mdStart.getTime()) / (1000 * 60);
          if (dischargeTime > 0) { if (!wardMap[ward]) wardMap[ward] = []; wardMap[ward].push(dischargeTime); }
        }
      });

      return Object.entries(wardMap).map(([ward, times]) => {
        const avgMinutes = times.reduce((sum, t) => sum + t, 0) / times.length;
        let withinTAT = 0, slightlyOver = 0, breached = 0;
        times.forEach((time) => {
          if (time <= TAT_THRESHOLDS.good) withinTAT++;
          else if (time <= TAT_THRESHOLDS.warning) slightlyOver++;
          else breached++;
        });
        const total = times.length;
        return { ward, avgMinutes, withinTAT: Math.round((withinTAT / total) * 100), slightlyOver: Math.round((slightlyOver / total) * 100), breached: Math.round((breached / total) * 100), totalCompleted: total };
      }).sort((a, b) => a.ward.localeCompare(b.ward));
    },
    enabled: currentBranchId != null && !!organizationId,
    refetchInterval: 30000,
  });
};
